import styles from "./styles.module.css";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { apiEventList, apiTowersCastleList, apiUserFamily } from "../../api";
import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";
import {
  Button,
  Card,
  Collapse,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  List,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";
import IconClose from "@mui/icons-material/Close";
import IconAttachFile from "@mui/icons-material/AttachFile";
import IconVisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  EventType,
  PermissionLevel,
  REGISTRATION_STATUS_ICON,
  RegistrationStatus,
} from "../../enums";
import FormCalendarRegistrationCreate from "../../components/FormCalendarRegistrationCreate/FormCalendarRegistrationCreate";
import { useAppContext } from "../../components/AppContext/AppContext";
import { capitalizeFirstLetter } from "../../utils/string";
import { useParams, useSearchParams } from "react-router-dom";
import { useCallback } from "react";
import Pagination from "@mui/material/Pagination";
import { API_EVENTS_LIST_PAGE_SIZE } from "../../consts";
import Map from "../../components/Map/Map";
import { getEventIcon, getEventUsers } from "../../utils/event";
import CastleBase from "../../components/CastleBase/CastleBase";
import IconArrowOutward from "@mui/icons-material/ArrowOutward";
import { LoaderClip } from "../LoaderClip/LoaderClip";

const GOOGLE_PHOTOS_URL = "https://photos.app.goo.gl/";
const GOOGLE_DRIVE_URL = "https://drive.google.com/drive/u/1/folders/";

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

function EventBox({ content, asList = false }: any) {
  if (asList) {
    return <Box>{content}</Box>;
  }

  return (
    <Grid className={styles.eventGrid} display="flex" alignItems="stretch">
      <Card variant="outlined" className={styles.eventCard}>
        {content}
      </Card>
    </Grid>
  );
}

function EventsBox({ content, asCard = false }: any) {
  const { t } = useTranslation("common");

  if (asCard) {
    return (
      <Grid container spacing={2} direction="column" flexWrap="nowrap">
        <Card variant="outlined">
          <Box className={styles.userTopBox}>
            <Typography variant="h6" fontWeight="600" component="div">
              {t("pages.user-dashboard.section.events.title")}
            </Typography>
          </Box>
          <Divider />
          <Box>
            <List className={styles.userFamilyList}>{content}</List>
          </Box>
        </Card>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} maxWidth="100%">
      {content}
    </Grid>
  );
}

export default function EventAgenda({
  isPast = false,
  asCard = false,
  asList = false,
  sharedDateFrom = undefined,
  setSharedDateFrom = undefined,
  sharedRegistrations = undefined,
  setSharedRegistrations = undefined,
}: any) {
  const [t, i18n] = useTranslation("common");
  const { token } = useParams();

  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("eventId");

  const theme = useTheme();
  const calendarMatchesSm = useMediaQuery(theme.breakpoints.up("sm"));
  const calendarMatchesMd = useMediaQuery(theme.breakpoints.up("md"));
  const calendarMatchesLg = useMediaQuery(theme.breakpoints.up("lg"));

  const { user } = useAppContext();

  const [eventPage, setEventPage] = React.useState(1);
  const [events, setEvents] = React.useState(undefined);
  const [family, setFamily] = React.useState(undefined);

  const [lastChanged, setLastChanged] = React.useState(Date.now());
  const [isFirstLoad, setIsFirstLoad] = React.useState(true);

  const [eventsRegistrationsOpen, setEventsRegistrationsOpen] = React.useState<{
    [key: string]: boolean;
  }>({});

  const [eventsCastlesOpen, setEventsCastlesOpen] = React.useState<{
    [key: string]: boolean;
  }>({});
  const [eventsCastlesModalOpen, setEventsCastlesModalOpen] =
    React.useState<string>(undefined);

  const [eventsMapOpen, setEventsMapOpen] = React.useState<{
    [key: string]: boolean;
  }>({});

  const [eventsCastles, setEventsCastles] = React.useState<{
    [key: string]: any;
  }>({});

  const [castlePinya, setCastlePinya] = React.useState(0);

  const handleCastlePinyaChange = (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setCastlePinya(newValue);
  };

  const handleEventRegistrationsClick = useCallback(
    (eventId: string) => {
      setEventsRegistrationsOpen((eventsRegistrationsOpen) => ({
        ...Object.fromEntries(
          Object.entries(eventsRegistrationsOpen).map(([k, v], i) => [
            k,
            false,
          ]),
        ),
        [eventId]: !eventsRegistrationsOpen[eventId],
      }));
      setEventsCastlesOpen({});
      setEventsMapOpen({});
    },
    [setEventsRegistrationsOpen, setEventsCastlesOpen, setEventsMapOpen],
  );

  const handleEventCastlesClick = useCallback(
    (eventId: string) => {
      if (calendarMatchesMd) {
        setEventsCastlesOpen((eventsCastlesOpen) => ({
          ...Object.fromEntries(
            Object.entries(eventsCastlesOpen).map(([k, v], i) => [k, false]),
          ),
          [eventId]: !eventsCastlesOpen[eventId],
        }));
        setEventsCastlesModalOpen(undefined);
      } else {
        setEventsCastlesModalOpen((eventsCastlesModalOpen) =>
          eventsCastlesModalOpen != null ? undefined : eventId,
        );
        setEventsCastlesOpen({});
      }
      setEventsRegistrationsOpen({});
      setEventsMapOpen({});
    },
    [
      setEventsCastlesOpen,
      setEventsRegistrationsOpen,
      setEventsCastlesModalOpen,
      setEventsMapOpen,
      calendarMatchesMd,
    ],
  );

  const handleEventMapClick = useCallback(
    (eventId: string) => {
      setEventsMapOpen((eventsMapOpen) => ({
        ...Object.fromEntries(
          Object.entries(eventsMapOpen).map(([k, v], i) => [k, false]),
        ),
        [eventId]: !eventsMapOpen[eventId],
      }));
      setEventsRegistrationsOpen({});
      setEventsCastlesOpen({});
    },
    [setEventsMapOpen, setEventsCastlesOpen, setEventsRegistrationsOpen],
  );

  React.useEffect(() => {
    apiEventList(
      eventPage,
      undefined,
      token,
      isPast ? null : new Date().toISOString().slice(0, 10),
      isPast ? new Date().toISOString().slice(0, 10) : null,
      undefined,
      undefined,
      undefined,
      undefined,
      [isPast ? "-time_from" : "time_from"],
    ).then((response) => {
      if (response.status === 200) {
        setEvents(response.data);
        if (
          setSharedDateFrom &&
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          const firstDate = new Date(response.data.results[0].time_from);
          if (
            sharedDateFrom === undefined ||
            sharedDateFrom.toISOString() !== firstDate.toISOString()
          ) {
            setSharedDateFrom(firstDate);
          }
        }
      }
    });
  }, [
    isPast,
    setEvents,
    setEventPage,
    i18n.resolvedLanguage,
    lastChanged,
    eventPage,
    token,
    setSharedDateFrom,
    sharedDateFrom,
  ]);

  React.useEffect(() => {
    if (user && events && events.count > 0) {
      for (let i = 0; i < events.results.length; i++) {
        const event = events.results[i];
        apiTowersCastleList(event.id).then((response) => {
          if (response.status === 200) {
            setEventsCastles((eventsCastles) => ({
              ...eventsCastles,
              [event.id]: response.data.results,
            }));
          }
        });
      }
    }
  }, [user, events, setEventsCastles]);

  React.useEffect(() => {
    if (user) {
      setFamily(user.family);
    } else if (token !== undefined) {
      apiUserFamily(token).then((response) => {
        if (response.status === 200) {
          setFamily(response.data);
        }
      });
    }
  }, [user, setFamily, handleEventMapClick, token, events, eventId]);

  React.useEffect(() => {
    if (isFirstLoad && events && events.results.length > 0) {
      setIsFirstLoad(false);
      handleEventMapClick(eventId ? eventId : events.results[0].id);
    }
  }, [handleEventMapClick, events, eventId, isFirstLoad]);

  const castlesModal =
    eventsCastlesModalOpen != null &&
    eventsCastles &&
    eventsCastles[eventsCastlesModalOpen];
  const castlesPublishedModal = castlesModal
    ? castlesModal.filter(
        (castle: any) =>
          castle.is_published ||
          (user && user.permission_level >= PermissionLevel.ADMIN),
      )
    : undefined;

  const eventsContent =
    events &&
    events.results.map((event: any, i: number, row: any) => {
      const castles = eventsCastles[event.id];
      const castlesPublished = castles
        ? castles.filter(
            (castle: any) =>
              castle.is_published ||
              (user && user.permission_level >= PermissionLevel.ADMIN),
          )
        : undefined;

      const registrationByEntityId = Object.fromEntries(
        event.registrations.map((registration: any) => [
          registration.entity.id,
          registration,
        ]),
      );

      const eventUsers =
        family &&
        getEventUsers(
          event,
          family.members.map((member: any) => member.user),
        );

      const eventContent = (
        <>
          <ListItemButton
            onClick={() => {
              if (!eventsMapOpen[event.id]) {
                handleEventMapClick(event.id);
              } else {
                setEventsRegistrationsOpen({});
                setEventsCastlesOpen({});
                setEventsMapOpen({});
              }
            }}
            disableTouchRipple={isPast}
            dense
          >
            <ListItemIcon className={styles.eventCardIcon}>
              {getEventIcon(event.type, event.modules, event.theme)}
            </ListItemIcon>
            <Box
              className={styles.eventCardInner}
              flexDirection={{ xs: "column", lg: "row" }}
              alignItems={{ xs: "start", lg: "center" }}
            >
              <ListItemText
                className={styles.eventCardListItem}
                disableTypography
                primary={
                  <Typography variant="body2">
                    {event.title +
                      ((event.type === EventType.REHEARSAL ||
                        event.type === EventType.WORKSHOP) &&
                      event.location !== null
                        ? " — " + event.location.name
                        : "")}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography variant="body2" color="textSecondary">
                      {capitalizeFirstLetter(
                        new Date(event.time_from).toLocaleDateString(
                          i18n.resolvedLanguage,
                          {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          },
                        ),
                      ) +
                        " " +
                        new Date(event.time_from).toTimeString().slice(0, 5) +
                        " ➔ " +
                        new Date(event.time_to).toTimeString().slice(0, 5)}
                    </Typography>
                    {castles && castles.length > 0 && (
                      <Box className={styles.eventCastlesBox}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          display="inline-flex"
                        >
                          {t("pages.calendar.section.agenda.castles")}
                          {": "}
                        </Typography>
                        {castles.map((castle: any, i: number, row: any) => {
                          return (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              className={styles.eventCastleBox}
                              key={i}
                            >
                              <span>{castle.name}</span>
                              {(castle.is_published ||
                                (user &&
                                  user.permission_level >=
                                    PermissionLevel.ADMIN)) && (
                                <>
                                  {" ("}
                                  {castle.is_published ? (
                                    <IconAttachFile />
                                  ) : (
                                    <IconVisibilityOff />
                                  )}
                                  {")"}
                                </>
                              )}
                              {i + 1 < row.length && ", "}
                            </Typography>
                          );
                        })}
                      </Box>
                    )}
                    {eventUsers &&
                      eventUsers.length > 0 &&
                      event.require_signup && (
                        <Collapse
                          in={!eventsRegistrationsOpen[event.id]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box className={styles.eventRegistrationsBox}>
                            {eventUsers.map((eventUser: any) => {
                              const registration =
                                registrationByEntityId[eventUser.entity.id];
                              return (
                                <Box
                                  key={eventUser.id}
                                  className={styles.eventRegistrationBox}
                                  style={{
                                    color: registration
                                      ? registration.status ===
                                        RegistrationStatus.ACTIVE
                                        ? "var(--mui-palette-success-main)"
                                        : "var(--mui-palette-error-main)"
                                      : isPast
                                        ? "var(--mui-palette-error-main)"
                                        : "var(--mui-palette-secondary-main)",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {
                                    REGISTRATION_STATUS_ICON[
                                      registration
                                        ? registration.status
                                        : isPast
                                          ? RegistrationStatus.CANCELLED
                                          : RegistrationStatus.REQUESTED
                                    ]
                                  }
                                  <Typography variant="body2">
                                    {family.members.length > 1
                                      ? eventUser.lastname
                                        ? eventUser.firstname +
                                          " " +
                                          eventUser.lastname
                                        : eventUser.firstname
                                      : registration &&
                                          registration.status ===
                                            RegistrationStatus.ACTIVE
                                        ? t(
                                            "pages.calendar.section.agenda.event.attendance-attending",
                                          )
                                        : t(
                                            "pages.calendar.section.agenda.event.attendance-not-attending",
                                          )}
                                  </Typography>
                                </Box>
                              );
                            })}{" "}
                          </Box>
                        </Collapse>
                      )}
                  </>
                }
              />
              {((!isPast && family && event.require_signup) ||
                (isPast &&
                  event.google_album &&
                  (event.google_album.photos_album ||
                    event.google_album.drive_album)) ||
                (user && castlesPublished && castlesPublished.length > 0)) && (
                <Stack
                  direction={
                    calendarMatchesSm
                      ? calendarMatchesLg
                        ? "column"
                        : "row"
                      : "column"
                  }
                  spacing={2}
                  marginLeft={{ xs: "0", lg: "16px" }}
                  marginTop="8px"
                  marginBottom="8px"
                  whiteSpace="nowrap"
                >
                  {isPast &&
                    event.google_album &&
                    event.google_album.photos_album &&
                    !event.google_album.drive_album && (
                      <Button
                        variant="contained"
                        type="submit"
                        style={{ width: "auto" }}
                        target={"_blank"}
                        disableElevation
                        disabled={
                          !event.google_album ||
                          !event.google_album.photos_album
                        }
                        href={
                          event.google_album &&
                          event.google_album.photos_album &&
                          GOOGLE_PHOTOS_URL +
                            event.google_album.photos_album.external_id
                        }
                      >
                        {t(
                          "pages.user-dashboard.section.events.show-photos-album",
                        )}
                        <IconArrowOutward className={styles.externalIcon} />
                      </Button>
                    )}
                  {isPast &&
                    event.google_album &&
                    event.google_album.drive_album && (
                      <Button
                        variant="contained"
                        type="submit"
                        style={{ width: "auto" }}
                        target={"_blank"}
                        disableElevation
                        disabled={
                          !event.google_album || !event.google_album.drive_album
                        }
                        href={
                          event.google_album &&
                          event.google_album.drive_album &&
                          GOOGLE_DRIVE_URL +
                            event.google_album.drive_album.external_id
                        }
                      >
                        {t(
                          "pages.user-dashboard.section.events.show-drive-album",
                        )}
                        <IconArrowOutward className={styles.externalIcon} />
                      </Button>
                    )}
                  {!isPast &&
                    eventUsers &&
                    eventUsers.length > 0 &&
                    event.require_signup &&
                    !event.require_approve && (
                      <Button
                        variant="contained"
                        type="submit"
                        style={{ width: "auto" }}
                        color={
                          eventsRegistrationsOpen[event.id]
                            ? "secondary"
                            : "primary"
                        }
                        disableElevation
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEventRegistrationsClick(event.id);
                        }}
                      >
                        {eventsRegistrationsOpen[event.id]
                          ? t(
                              "pages.calendar.section.agenda.event.attendance-hide",
                            )
                          : t(
                              "pages.calendar.section.agenda.event.attendance-show",
                            )}
                      </Button>
                    )}
                  {eventUsers &&
                    eventUsers.length > 0 &&
                    user &&
                    castlesPublished &&
                    castlesPublished.length > 0 && (
                      <Button
                        variant="contained"
                        type="submit"
                        style={{ width: "auto" }}
                        color={
                          eventsCastlesOpen[event.id] ? "secondary" : "primary"
                        }
                        disableElevation
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEventCastlesClick(event.id);
                        }}
                      >
                        {eventsCastlesOpen[event.id]
                          ? t(
                              "pages.calendar.section.agenda.event.castles-hide",
                            )
                          : t(
                              "pages.calendar.section.agenda.event.castles-show",
                            )}
                      </Button>
                    )}
                </Stack>
              )}
            </Box>
          </ListItemButton>
          {eventUsers && eventUsers.length > 0 && event.require_signup && (
            <Collapse
              in={eventsRegistrationsOpen[event.id]}
              timeout="auto"
              unmountOnExit
            >
              <Divider />
              <Box className={styles.calendarAttendanceUpdate}>
                <Typography fontWeight={600}>
                  {t("pages.calendar.agenda.attendance")}
                </Typography>
                <FormCalendarRegistrationCreate
                  event={event}
                  users={eventUsers}
                  token={token}
                  setLastChanged={setLastChanged}
                  sharedRegistrations={sharedRegistrations}
                  setSharedRegistrations={setSharedRegistrations}
                />
              </Box>
            </Collapse>
          )}
          {eventUsers &&
            eventUsers.length > 0 &&
            castles &&
            castles.length > 0 && (
              <Collapse
                in={eventsCastlesOpen[event.id]}
                timeout="auto"
                unmountOnExit
              >
                <Divider />
                <Tabs
                  value={castlePinya}
                  onChange={handleCastlePinyaChange}
                  variant="scrollable"
                  scrollButtons={false}
                  allowScrollButtonsMobile
                  indicatorColor="primary"
                  TabIndicatorProps={{
                    style: { display: "none" },
                  }}
                  sx={{
                    ".Mui-selected": {
                      backgroundColor: "var(--mui-palette-primary-main)",
                      color:
                        "var(--mui-palette-primary-contrastText) !important",
                    },
                  }}
                >
                  {castlesPublished.map((castle: any) => (
                    <Tab
                      label={
                        <Typography
                          variant="body2"
                          component="div"
                          className={styles.tabLabelText}
                        >
                          {castle.name}
                          {!castle.is_published && <IconVisibilityOff />}
                        </Typography>
                      }
                    />
                  ))}
                </Tabs>
                <Divider />
                {castlesPublished.map((castle: any, ix: number) => (
                  <TabPanel value={castlePinya} index={ix}>
                    <CastleBase castle={castle} />
                  </TabPanel>
                ))}
              </Collapse>
            )}
          {!isPast && event.location !== null && (
            <Collapse in={eventsMapOpen[event.id]} timeout="auto" unmountOnExit>
              <Divider />
              <Box className={styles.calendarMap}>
                <Map
                  location={event.location}
                  coordinates={[
                    event.location.coordinate_lat,
                    event.location.coordinate_lon,
                  ]}
                  connections={event.location.connections}
                  eventType={event.type}
                  eventTheme={event.theme}
                />
              </Box>
            </Collapse>
          )}

          {asList && i + 1 < row.length && <Divider />}
        </>
      );

      return <EventBox content={eventContent} asList={asList} />;
    });

  return (
    <>
      {events ? (
        <>
          {castlesPublishedModal && castlesPublishedModal.length > 0 && (
            <Modal
              open={eventsCastlesModalOpen != null}
              onClose={handleEventCastlesClick}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <Box className={styles.modalBox}>
                <Tabs
                  value={castlePinya}
                  onChange={handleCastlePinyaChange}
                  variant="scrollable"
                  scrollButtons={false}
                  allowScrollButtonsMobile
                  indicatorColor="primary"
                  TabIndicatorProps={{
                    style: { display: "none" },
                  }}
                  className={styles.modelTabs}
                  sx={{
                    ".Mui-selected": {
                      backgroundColor: "var(--mui-palette-primary-main)",
                      color:
                        "var(--mui-palette-primary-contrastText) !important",
                    },
                  }}
                >
                  {castlesPublishedModal.map((castle: any) => (
                    <Tab
                      label={
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          component="span"
                          className={styles.tabLabelText}
                        >
                          {castle.name}
                          {!castle.is_published && <IconVisibilityOff />}
                        </Typography>
                      }
                      className={styles.modalTabLabel}
                    />
                  ))}
                  <Button
                    variant="contained"
                    type="submit"
                    color="primary"
                    disableElevation
                    className={styles.modalClose}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEventCastlesClick(eventsCastlesModalOpen);
                    }}
                  >
                    <IconClose />
                  </Button>
                </Tabs>
                {castlesPublishedModal.map((castle: any, ix: number) => (
                  <TabPanel value={castlePinya} index={ix}>
                    <Box className={styles.modalTabContent}>
                      <CastleBase castle={castle} />
                    </Box>
                  </TabPanel>
                ))}
              </Box>
            </Modal>
          )}
          {events && events.count > 0 && (
            <Grid
              container
              spacing={2}
              width="100%"
              direction="column"
              flexWrap="nowrap"
            >
              <EventsBox content={eventsContent} asCard={asCard} />
              {events &&
                events.results.length > 0 &&
                (eventPage !== 1 || events.count > events.results.length) && (
                  <Stack alignItems="center">
                    <Pagination
                      count={Math.ceil(
                        events.count / API_EVENTS_LIST_PAGE_SIZE,
                      )}
                      onChange={(e: any, value: number) => setEventPage(value)}
                    />
                  </Stack>
                )}
            </Grid>
          )}
        </>
      ) : (
        <Box className={styles.pageLoader}>
          <LoaderClip />
        </Box>
      )}
    </>
  );
}
